'use client';

import { marked } from 'marked';
import type { ComponentProps } from 'react';
import { memo, useMemo } from 'react';
import { Streamdown } from 'streamdown';

export type MemoizedMarkdownProps = {
  /** Stable id for keying markdown blocks (usually message id). */
  id: string;
  /** Raw markdown content. */
  content: string;
  /** ClassName applied to each rendered markdown block. */
  className?: string;
  /** Optional Streamdown props (e.g. `components` overrides). */
  streamdownProps?: Omit<ComponentProps<typeof Streamdown>, 'children'>;
};

function parseMarkdownIntoBlocks(markdown: string): string[] {
  const tokens = marked.lexer(markdown);
  return tokens
    .map((token) => token.raw)
    .filter((raw) => typeof raw === 'string' && raw.trim().length > 0);
}

type MemoizedMarkdownBlockProps = Pick<MemoizedMarkdownProps, 'content' | 'className'> &
  Omit<ComponentProps<typeof Streamdown>, 'children'>;

const MemoizedMarkdownBlock = memo(
  ({ content, className, ...props }: MemoizedMarkdownBlockProps) => {
    return (
      <Streamdown className={className} {...props}>
        {content}
      </Streamdown>
    );
  },
  (prevProps, nextProps) =>
    prevProps.content === nextProps.content && prevProps.className === nextProps.className
);

MemoizedMarkdownBlock.displayName = 'MemoizedMarkdownBlock';

export const MemoizedMarkdown = memo(
  ({ content, id, className, streamdownProps }: MemoizedMarkdownProps) => {
    const blocks = useMemo(() => parseMarkdownIntoBlocks(content), [content]);

    return blocks.map((block, index) => (
      <MemoizedMarkdownBlock
        content={block}
        className={className}
        key={`${id}-block_${index}`}
        {...streamdownProps}
      />
    ));
  }
);

MemoizedMarkdown.displayName = 'MemoizedMarkdown';
